import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, FormControl, FormControlLabel, FormGroup, FormHelperText, InputAdornment, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useContent } from "../../logic";
import { useLocation, useParams } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { PatternFormat } from "react-number-format";
import { useOrganization } from '../../components/OrganizationProvider';
import { useGetOrder } from "../../logic/useApiQuery";
import { useUpdateSmsPreferenceStatus } from "../../logic/useApiMutation";
import { useOptimisticUpdatePortalOrder } from "../../logic/optimisticUpdates";
import { isAdhocPortalOrder } from "../../utils/isAdhocPortalOrder";
import { parsePhoneNumber } from "../../utils/parsePhoneNumber";
import { isValidPhoneNumber } from "libphonenumber-js";


export function SmsPreferenceStatusDialog({ open, setOpen, back }: {
  open: boolean;
  setOpen: Function;
  back?: Function;
}) {
  const { closingId } = useParams()
  const { search } = useLocation()
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search ?? '')
  const { ClosingsText } = useContent()
  const { CompanyDisplayName } = useOrganization()
  const [phoneError, setPhoneError] = useState<PhoneError>(null)
  const [consentTOSError, setConsentTOSError] = useState(false)
  const [consentError, setConsentError] = useState(false)
  const { ClosingText } = useContent()
  const initialConsent = (globalOrderData && !isAdhocPortalOrder(globalOrderData.Order) && globalOrderData?.Order.SmsPreferenceStatus?.consent) ?? false
  const [consentChoice, setConsentChoice] = useState<'consent' | 'noConsent' | null>(initialConsent ? 'consent' : null)

  type PhoneError = 'invalid' | 'mismatch' | null
  const PHONE_HELPER_TEXT: Record<Exclude<PhoneError, null>, React.ReactNode> = {
    invalid: 'Please enter a valid 10-digit phone number.',
    mismatch: <><strong>Phone number not eligible.</strong><br />
    The phone number provided is not currently eligible for SMS updates at this time. <br />
    Please confirm this is the phone number <strong>{CompanyDisplayName}</strong> has on file for this {ClosingText}.</>
  }

  const PHONE_NUMBER_EXAMPLE = '555-123-4567'

  const initialPhoneNumber = (globalOrderData && !isAdhocPortalOrder(globalOrderData.Order) && globalOrderData?.Order.SmsPreferenceStatus?.phoneNumber) || ''

  useEffect(() => {
    if (!open) return

    setPhoneError(null)
    setConsentTOSError(false)
    setConsentError(false)
    // setConsentChecked(initialConsent)
  }, [open])

  const { optimisticUpdatePortalOrder } = useOptimisticUpdatePortalOrder()

  const { isLoading: loading, error: updateSmsPreferenceStatusError, mutate } = useUpdateSmsPreferenceStatus(search, {
    onSuccess: (data, variables) => {
      const phoneNumber = 'phoneNumber' in variables ? variables.phoneNumber : initialPhoneNumber
      if (!data.matchingPhoneNumber) {
        setPhoneError('mismatch')
        return
      }
      optimisticUpdatePortalOrder({
        orderKey: closingId ?? '',
        search,
        patch: {
          SmsPreferenceStatus: {
            consent: variables.consent,
            phoneNumber: parsePhoneNumber(phoneNumber).redacted,
          }
        }
      });
      setOpen(false)
    }
  })

  if (!globalOrderData) { return null }
  if (isAdhocPortalOrder(globalOrderData.Order)) { return null }

  const handleSave = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setPhoneError(null)
    setConsentTOSError(false)
    setConsentError(false)
    const formData = new FormData(event.currentTarget)
    const consent = formData.get('consentChoice') === 'consent' 
    const noConsent = formData.get('consentChoice') === 'noConsent' 
    const consentTOS = formData.get('consentTOSCheckbox') === 'on'
    const phoneNumber = ((formData.get('phoneNumber') as string) || '').trim()
    if (!consent && !noConsent) {
      return
    }

    if (!consent) {
      mutate({ consent: false, orderKey: closingId || '' })
      return
    }
    let hasError = false
    if (!consentTOS) {
      setConsentTOSError(!consentTOS)
      setConsentError(!consent)
      hasError = true
    }

    if (!phoneNumber || (phoneNumber && !isValidPhoneNumber(phoneNumber, 'US'))) {
      setPhoneError('invalid')
      hasError = true
    }

    if (hasError) {
      return
    }

    mutate({ phoneNumber, consent, orderKey: closingId || '' })
  }

  return (
    <Dialog
      key="userCancel"
      open={open}
      onClose={() => setOpen(false)}
    >
      <form onSubmit={handleSave}>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }} variant='h6'>
            SMS Settings
          </DialogContentText>
          <Typography>
            Please confirm that you consent to receive informational SMS notifications & alerts about your {ClosingsText} from {CompanyDisplayName} via CloseSimple regarding your real estate closing:
          </Typography>
          <FormGroup>
            <FormControl error={consentError} sx={{ py: 2 }}>
              <RadioGroup
                value={consentChoice}
                onChange={e => setConsentChoice(e.target.value as 'consent' | 'noConsent')}
                name="consentChoice"
              >
                <FormControlLabel value="consent" control={<Radio />} label={<Typography>I consent to receive SMS notifications & alerts about my {ClosingsText} from {CompanyDisplayName}.</Typography>}/>
                <FormControlLabel value="noConsent" control={<Radio />} label={<Typography>I do not consent to receive SMS notifications & alerts about my {ClosingsText} from {CompanyDisplayName}.</Typography>} />
              </RadioGroup>
              {consentError && <FormHelperText>Please provide consent to receive SMS updates.</FormHelperText>}
            </FormControl>
            <Box sx={{ visibility: consentChoice === 'consent' ? 'visible' : 'hidden' }}>
              <PatternFormat
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">+1</InputAdornment>
                  ),
                }}
                customInput={TextField}
                name="phoneNumber"
                label="Phone Number"
                margin="normal"
                fullWidth
                format="###-###-####"
                placeholder={PHONE_NUMBER_EXAMPLE}
                error={!!phoneError}
                helperText={(phoneError && PHONE_HELPER_TEXT[phoneError]) ?? `Example: ${PHONE_NUMBER_EXAMPLE}`}
                inputProps={{ inputMode: 'numeric' }}
                onChange={() => setPhoneError(null)}
              />
              <FormControl error={consentTOSError}>
                <FormControlLabel
                  control={<Checkbox name="consentTOSCheckbox" defaultChecked={false} />}
                  label={<span>I have reviewed the SMS <a href="https://www.closesimple.com/sms-terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="https://www.closesimple.com/sms-messaging-privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</span>}
                />
                {consentTOSError && <FormHelperText>Please review and accept the Terms and Conditions.</FormHelperText>}
              </FormControl>
              <Typography variant="body2" sx={{ mt: 2, ml: 4, display: 'block', color: 'text.secondary' }}>
                Message frequency varies. Message & data rates may apply. <strong>You can reply HELP for help or STOP to unsubscribe at any time.</strong>
              </Typography>
            </Box>
          </FormGroup>
        </DialogContent>
        {updateSmsPreferenceStatusError && (
          <DialogContent>
            <DialogContentText variant="body1" color="error">
              {updateSmsPreferenceStatusError.userErrorMessage}
            </DialogContentText>
          </DialogContent>
        )}
        <DialogActions>
          {back && <Button type="button" variant="rounded" onClick={() => setOpen(false)}>Close</Button>}
          {back && <Button type="button" variant="rounded" onClick={() => back()}>Back</Button>}
          <LoadingButton loading={loading} type="submit" variant="contained">Save</LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}
